import React, { useContext, useEffect } from "react";
import { Outlet, useParams } from "react-router";
import { HeaderLayout } from "../../../components";
import { useSession } from "../../../hooks";
import { useGetTeam } from "../../../http";
import { ITeam } from "../../../models";
import { setTitlePrefix } from "../../../utilities";
import { TeamActions, TeamNavBar, TeamTitle } from "./components";
import { Skeleton, Stack } from "@mui/material";

interface IParams {
  slug: string;
}

interface ITeamPageLayoutContext {
  team: ITeam;
}

const TeamPageLayoutContext = React.createContext<ITeamPageLayoutContext | undefined>(undefined);

const TeamPageLayout = () => {
  const { slug } = useParams<keyof IParams>() as IParams;
  const { userId } = useSession();
  const { data: team, isLoading } = useGetTeam(slug, userId);

  useEffect(() => {
    setTitlePrefix(team?.name);
  }, [team?.name]);

  if (isLoading) {
    return (
      <HeaderLayout
        title={<Skeleton variant="rectangular" width={400} height={40} />}
        navbar={<Skeleton variant="rectangular" width={600} height={15} sx={{ my: 0.5, ml: 1 }} />}
        headerContainerProps={{
          sx: {
            alignItems: "flex-end",
            pb: 0,
          },
        }}
      >
        <Stack spacing={1}>
          <Skeleton variant="rectangular" width={450} />
          <Skeleton variant="rectangular" width={600} />
          <Skeleton variant="rectangular" width={500} />
        </Stack>
      </HeaderLayout>
    );
  }

  if (!isLoading && team) {
    return (
      <TeamPageLayoutContext.Provider value={{ team }}>
        <HeaderLayout
          title={<TeamTitle showAdministratorsTooltip />}
          navbar={!isLoading && <TeamNavBar />}
          actions={!isLoading && <TeamActions />}
          headerContainerProps={{
            sx: {
              alignItems: "flex-end",
              pb: 0,
            },
          }}
        >
          <Outlet />
        </HeaderLayout>
      </TeamPageLayoutContext.Provider>
    );
  }

  return null;
};

const useTeamPageLayoutContext = () => {
  const context = useContext(TeamPageLayoutContext);

  if (context === undefined) {
    throw new Error("TeamRouteContext is not initialized.");
  }

  return context;
};

export { TeamPageLayout, useTeamPageLayoutContext };
